import { Link, Outlet, createRootRoute, useRouterState } from "@tanstack/react-router";
import {
	Briefcase,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	ClipboardCheck,
	ClipboardList,
	Database,
	FlaskConical,
	GitBranch,
	Info,
	type LucideIcon,
	Map as MapIcon,
	Network,
	ShieldCheck,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { IntercomMessenger } from "@/components/intercom-messenger";
import { PasswordGate } from "@/components/password-gate";
import { PLAN_NAV_GROUPS, type PlanGroupKey } from "@/components/plan-lab/page-registry";
import { ToastProvider } from "@/components/ui/toast";
import { trackEvent } from "@/lib/analytics";
import { usePlanEnabled } from "@/lib/api/plan-lab";
import { cn } from "@/lib/utils";

export const Route = createRootRoute({
	component: RootLayout,
});

function usePageViewTracking() {
	const location = useRouterState({ select: (s) => s.location });
	const enterTimeRef = useRef<number>(performance.now());
	const lastPathRef = useRef<string>(location.pathname);

	useEffect(() => {
		const previousPath = lastPathRef.current;
		const previousEnterTime = enterTimeRef.current;
		const now = performance.now();
		const durationSec = (now - previousEnterTime) / 1000;

		// 路由切换：先把 "上一页停留时长" 发出，再开始计时新页
		trackEvent({
			event: "admin_page_view",
			properties: {
				mxx_id: pathToMxxId(previousPath),
				url: previousPath,
				duration_sec: Math.max(0, durationSec),
			},
		});

		lastPathRef.current = location.pathname;
		enterTimeRef.current = now;
	}, [location.pathname]);
}

/** 路径 → Mxx ID（T11 §3.2 路由表 + §12.3 mxx_id 字段） */
function pathToMxxId(path: string): string {
	if (path === "/" || path === "") return "global";
	if (path.startsWith("/debug")) return "M01";
	if (path.startsWith("/trace-search")) return "M01";
	if (path.startsWith("/sessions-review")) return "M06";
	if (path.startsWith("/intent")) return "M02";
	if (path.startsWith("/playbook")) return "M03";
	if (path.startsWith("/skill-tool")) return "M04";
	if (path.startsWith("/prompt-q")) return "M05";
	if (path.startsWith("/quality")) return "P10-S1-AB";
	if (path.startsWith("/sessions")) return "M06";
	if (path.startsWith("/eval-rules")) return "M08";
	if (path.startsWith("/eval")) return "M07";
	if (path.startsWith("/external-tests-guide")) return "EXT-T";
	if (path.startsWith("/external-tests")) return "EXT-T";
	if (path.startsWith("/knowledge")) return "M09";
	if (path.startsWith("/pipeline-testbench")) return "P2";
	if (path.startsWith("/test-datasets")) return "P3";
	if (path.startsWith("/dictionary")) return "P4";
	if (path.startsWith("/runtime-closure")) return "P5";
	if (path.startsWith("/memory")) return "M10";
	if (path.startsWith("/merchants")) return "M11";
	if (path.startsWith("/dashboard")) return "M12";
	if (path.startsWith("/health")) return "M13";
	if (path.startsWith("/marketing")) return "M14";
	if (path.startsWith("/dev-console")) return "dev_console";
	return "global";
}

/** Dev Console nav 可见性 · VITE_DEV_CONSOLE_ENABLED=1 时显示（dev/staging build 注入） */
const DEV_CONSOLE_NAV_VISIBLE = import.meta.env.VITE_DEV_CONSOLE_ENABLED === "1";

type AdminRoutePath =
	| "/"
	| "/runtime-closure"
	| "/dev-console"
	| "/pipeline-testbench"
	| "/debug"
	| "/trace-search"
	| "/loop-mvp-lab"
	| "/plan-lab"
	| "/health"
	| "/sessions-review"
	| "/intent"
	| "/playbook"
	| "/skill-tool"
	| "/knowledge"
	| "/prompt-q"
	| "/quality"
	| "/eval"
	| "/external-tests-guide"
	| "/external-tests"
	| "/test-datasets"
	| "/dictionary";

type SidebarItem = {
	label: string;
	to?: AdminRoutePath;
	hash?: string;
	status?: "todo";
	testId?: string;
};

type SidebarLinkItem = SidebarItem & { to: AdminRoutePath };

type SidebarGroup = {
	id: string;
	label: string;
	description: string;
	icon: LucideIcon;
	testId: string;
	items: SidebarItem[];
};

// C7-S1 工作台:① Loop 模式(验证/评测,默认·后续迭代都在这)② Workflow 模式(既有配置/监控,停放不迭代)。
// 归属由 group.id 前缀派生:loop-* → Loop 模式,其余 → Workflow 模式。
// V3 PR0:加 Plan 模式(plan-* → Plan;D-19)。后台页面对齐批 2(D-11):三段顺序改 Plan ｜ Loop ｜ Workflow,文字只留英文;首页 / 仍随路由高亮 Workflow。
type Workbench = "loop" | "plan" | "workflow";
const groupWorkbench = (id: string): Workbench =>
	id.startsWith("loop-") ? "loop" : id.startsWith("plan-") ? "plan" : "workflow";
const WORKBENCHES = ["plan", "loop", "workflow"] as const;
const WORKBENCHES_NO_PLAN = ["loop", "workflow"] as const;
const WORKBENCH_LABEL: Record<Workbench, string> = {
	plan: "Plan",
	loop: "Loop",
	workflow: "Workflow",
};

/** Plan 各一级的图标（分组、顺序、页面都在页面注册表 `components/plan-lab/page-registry.json`） */
const PLAN_GROUP_ICON: Record<PlanGroupKey, LucideIcon> = {
	overview: MapIcon,
	modes: FlaskConical,
	results: ClipboardCheck,
	"eval-config": ShieldCheck,
	chain: Network,
	business: Briefcase,
	knowledge: ClipboardList,
	base: Database,
};
const PLAN_ROUTE: AdminRoutePath = "/plan-lab";

/** Plan 段侧栏分组：菜单重组（§八 8.2）后由注册表生成，hash 与 testId 不变（旧链接照常能开） */
const PLAN_SIDEBAR_GROUPS: SidebarGroup[] = PLAN_NAV_GROUPS.map((g) => ({
	id: g.navId,
	label: g.label,
	description: g.answer,
	icon: PLAN_GROUP_ICON[g.key],
	testId: g.testId,
	items: g.pages.map((p) => ({
		to: PLAN_ROUTE,
		hash: p.id,
		label: p.name,
		testId: `nav-plan-${p.id}`,
	})),
}));

const SIDEBAR_GROUPS = [
	{
		id: "loop",
		label: "运营总览",
		description: "阶段、风险和下一步",
		icon: ClipboardCheck,
		testId: "nav-group-loop",
		items: [{ to: "/", label: "闭环总览", testId: undefined }],
	},
	{
		id: "runtime",
		label: "接入状态",
		description: "依赖、权限和真实边界",
		icon: ShieldCheck,
		testId: "nav-group-runtime",
		items: [
			{ to: "/runtime-closure", label: "运行依赖", testId: "nav-p5" },
			...(DEV_CONSOLE_NAV_VISIBLE
				? [{ to: "/dev-console" as const, label: "开发验证", testId: "nav-dev-console" }]
				: []),
		],
	},
	{
		id: "loop-overview",
		label: "链路总览",
		description: "整条 loop 怎么转,点节点看定义",
		icon: MapIcon,
		testId: "nav-group-loop-overview",
		items: [
			{ to: "/loop-mvp-lab", hash: "overview", label: "链路总览", testId: "nav-loop-overview" },
			{
				to: "/loop-mvp-lab",
				hash: "io-contract",
				label: "IO 一致性体检",
				testId: "nav-loop-io-contract",
			},
		],
	},
	{
		id: "loop-test-modes",
		label: "评测模式",
		description: "人工提问 / 固定用例 / 模拟场景",
		icon: FlaskConical,
		testId: "nav-group-loop-modes",
		items: [
			{
				to: "/loop-mvp-lab",
				hash: "playground",
				label: "人工提问",
				testId: "nav-loop-playground",
			},
			{
				to: "/loop-mvp-lab",
				hash: "scenarios",
				label: "固定用例",
				testId: "nav-loop-scenarios",
			},
			{ to: "/loop-mvp-lab", hash: "eval", label: "模拟场景", testId: "nav-loop-eval" },
		],
	},
	{
		id: "loop-results",
		label: "数据分析",
		description: "指标统计 / 运行记录 / 评测记录",
		icon: ClipboardCheck,
		testId: "nav-group-loop-results",
		items: [
			{
				to: "/loop-mvp-lab",
				hash: "stats",
				label: "指标统计",
				testId: "nav-loop-stats",
			},
			{ to: "/loop-mvp-lab", hash: "history", label: "运行记录", testId: "nav-loop-history" },
			{
				to: "/loop-mvp-lab",
				hash: "eval-records",
				label: "评测记录",
				testId: "nav-loop-eval-records",
			},
			{
				to: "/loop-mvp-lab",
				hash: "rca-records",
				label: "归因记录",
				testId: "nav-loop-rca-records",
			},
			{
				to: "/loop-mvp-lab",
				hash: "cx-records",
				label: "CX 评分",
				testId: "nav-loop-cx-records",
			},
			{
				to: "/loop-mvp-lab",
				hash: "guidance-stats",
				label: "指南效果",
				testId: "nav-loop-guidance-stats",
			},
			{
				to: "/loop-mvp-lab",
				hash: "method-skills",
				label: "优化打法",
				testId: "nav-loop-method-skills",
			},
		],
	},
	{
		id: "loop-eval-config",
		label: "评测配置",
		description: "模拟场景库 / 评测数据 / 提示词 / 规则",
		icon: Info,
		testId: "nav-group-loop-eval-config",
		items: [
			{
				to: "/loop-mvp-lab",
				hash: "eval-scenarios",
				label: "模拟场景库",
				testId: "nav-loop-eval-scenarios",
			},
			{
				to: "/loop-mvp-lab",
				hash: "reference-data",
				label: "评测数据",
				testId: "nav-loop-eval-data",
			},
			{
				to: "/loop-mvp-lab",
				hash: "eval-machine-customer",
				label: "模拟提示词",
				testId: "nav-loop-machine-customer",
			},
			{
				to: "/loop-mvp-lab",
				hash: "eval-judges",
				label: "评测提示词",
				testId: "nav-loop-judges",
			},
			{
				to: "/loop-mvp-lab",
				hash: "eval-rules",
				label: "评测规则",
				testId: "nav-loop-eval-rules",
			},
			{
				to: "/loop-mvp-lab",
				hash: "rca-standard",
				label: "归因标准",
				testId: "nav-loop-rca-standard",
			},
			{
				to: "/loop-mvp-lab",
				hash: "cx-standard",
				label: "CX 评分标准",
				testId: "nav-loop-cx-standard",
			},
		],
	},
	{
		id: "loop-answer-config",
		label: "应答配置",
		description: "应答提示词 / 意图 / SKILL / 规则 / 应答指南",
		icon: Network,
		testId: "nav-group-loop-answer-config",
		items: [
			{
				to: "/loop-mvp-lab",
				hash: "prompts",
				label: "应答提示词",
				testId: "nav-loop-prompts",
			},
			{
				to: "/loop-mvp-lab",
				hash: "reference-intent",
				label: "意图",
				testId: "nav-loop-intent",
			},
			{
				to: "/loop-mvp-lab",
				hash: "reference-skill",
				label: "SKILL",
				testId: "nav-loop-skill",
			},
			{
				to: "/loop-mvp-lab",
				hash: "reference-rules",
				label: "规则",
				testId: "nav-loop-answer-rules",
			},
			{
				to: "/loop-mvp-lab",
				hash: "guidance",
				label: "应答指南",
				testId: "nav-loop-guidance",
			},
			{
				to: "/loop-mvp-lab",
				hash: "connect",
				label: "连接",
				testId: "nav-loop-connect",
			},
		],
	},
	{
		id: "response",
		label: "应答测试",
		description: "单条运行和历史回放",
		icon: GitBranch,
		testId: "nav-group-response",
		items: [
			{ to: "/pipeline-testbench", label: "链路测试台", testId: "nav-p2" },
			{ to: "/debug", label: "运行记录", testId: undefined },
			{ to: "/trace-search", label: "Trace 检索", testId: "nav-trace-search" },
			{ to: "/sessions-review", label: "会话复盘", testId: "nav-m06" },
			{ to: "/health", label: "系统健康", testId: "nav-m13" },
		],
	},
	{
		id: "intent",
		label: "意图分类",
		description: "客户在问什么",
		icon: ClipboardList,
		testId: "nav-group-intent",
		items: [
			{ to: "/intent", label: "分类管理", hash: "intent-management", testId: "nav-m02" },
			{ to: "/intent", label: "分类测试", hash: "intent-test", testId: undefined },
		],
	},
	{
		id: "playbook",
		label: "处理方案",
		description: "意图到方案和 fallback",
		icon: GitBranch,
		testId: "nav-group-playbook",
		items: [
			{ to: "/playbook", label: "方案管理", hash: "playbook-management", testId: "nav-m03" },
			{ to: "/playbook", label: "方案测试", hash: "playbook-test", testId: undefined },
		],
	},
	{
		id: "skills",
		label: "工具与动作",
		description: "只读、写入和 dry-run",
		icon: ShieldCheck,
		testId: "nav-group-skills",
		items: [
			{ to: "/skill-tool", label: "Skill 能力包", hash: "skill-catalog", testId: "nav-m04" },
			{
				to: "/skill-tool",
				label: "Skill 调试",
				hash: "skill-action-verify",
				testId: undefined,
			},
			{ to: "/skill-tool", label: "写动作审计", hash: "write-actions", testId: undefined },
		],
	},
	{
		id: "knowledge",
		label: "知识与证据",
		description: "知识源和检索与验证",
		icon: Network,
		testId: "nav-group-knowledge",
		items: [
			{ to: "/knowledge", label: "知识源", hash: "knowledge-sources", testId: "nav-m09" },
			{
				to: "/knowledge",
				label: "检索与验证",
				hash: "knowledge-search-verify",
				testId: undefined,
			},
		],
	},
	{
		id: "prompt",
		label: "提示词与话术",
		description: "Prompt、模板和预览",
		icon: ClipboardList,
		testId: "nav-group-prompt",
		items: [
			{ to: "/prompt-q", label: "提示词管理", hash: "prompt-management", testId: "nav-m05" },
			{
				to: "/prompt-q",
				label: "沙盒与发布前评测",
				hash: "prompt-sandbox",
				testId: undefined,
			},
		],
	},
	{
		id: "quality",
		label: "质量策略",
		description: "阻断、改写和转人工",
		icon: ShieldCheck,
		testId: "nav-group-quality",
		items: [{ to: "/quality", label: "策略工作台", testId: "nav-quality" }],
	},
	{
		id: "evaluation",
		label: "评测运营",
		description: "样本、评测和问题闭环",
		icon: ClipboardCheck,
		testId: "nav-group-effect",
		items: [
			{ to: "/eval", label: "场景指标", hash: "scenario-metrics", testId: undefined },
			{ to: "/test-datasets", label: "样本库", testId: "nav-p3" },
			{ to: "/eval", label: "评测运行", hash: "eval-run", testId: "nav-m07" },
			{ to: "/eval", label: "体验打分(L0)", hash: "cx-score", testId: "nav-m07-cx-score" },
			{ to: "/eval", label: "问题闭环", hash: "eval-badcase-loop", testId: undefined },
			{
				to: "/eval",
				label: "实验治理",
				hash: "ab-experiment-governance",
				testId: undefined,
			},
		],
	},
	{
		id: "external-tests",
		label: "外挂测试",
		description: "tau2 retail 外部验证",
		icon: ClipboardCheck,
		testId: "nav-group-external-tests",
		items: [
			{ to: "/external-tests-guide", label: "测试说明", testId: "nav-external-tests-guide" },
			{ to: "/external-tests", label: "测试操作", testId: "nav-external-tests" },
		],
	},
	{
		id: "contracts",
		label: "接口与字段",
		description: "接口、字段和一致性",
		icon: Info,
		testId: "nav-group-contract",
		items: [
			{ label: "接口文档", status: "todo" },
			{ to: "/dictionary", label: "字段字典", testId: "nav-p4" },
			{ label: "一致性审计", status: "todo" },
		],
	},
	// Plan 段：读页面注册表生成（菜单重组 §八，不再手写；分组、顺序、页名都只在注册表 ＋ plan-tabs.ts）
	...PLAN_SIDEBAR_GROUPS,
] satisfies SidebarGroup[];

function createInitialExpandedGroups(): Record<string, boolean> {
	return Object.fromEntries(SIDEBAR_GROUPS.map((group) => [group.id, false]));
}

function isRouteActive(pathname: string, to?: AdminRoutePath): boolean {
	if (!to) return false;
	if (to === "/") return pathname === "/";
	return pathname === to || pathname.startsWith(`${to}/`);
}

function isSidebarItemActive(pathname: string, hash: string, item: SidebarItem): boolean {
	if (!isRouteActive(pathname, item.to)) return false;
	if (!item.hash) return !hash || item.to === "/";
	return hash.replace(/^#/, "") === item.hash;
}

function getDirectSidebarItem(group: SidebarGroup): SidebarLinkItem | undefined {
	if (group.items.length !== 1) return undefined;
	const item = group.items[0];
	if (!item?.to) return undefined;
	return item as SidebarLinkItem;
}

function RootLayout() {
	usePageViewTracking();
	const location = useRouterState({ select: (s) => s.location });
	const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
	const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
		createInitialExpandedGroups,
	);
	// C7-S1 工作台页签 · 默认随当前页(方案B):验证页(loop-mvp-lab)→ Loop,其余(含首页/配置页)→ Workflow。
	// 进验证台自动选 Loop;在首页/配置页时 Workflow 激活(其组可见,老导航/测试不被藏)。手动点页签可切换,导航后再随路由同步。
	// 随路由:loop-mvp-lab → Loop(工作页默认 Loop),其余页 → Workflow(保留工作流导航,避免藏掉入口)
	// V3 PR0 熔断隐藏:plan 元信息接口回 404(PLAN_LAB_ENABLED=false)→ 不显示 Plan 段
	const planEnabled = usePlanEnabled();
	const routeWorkbench: Workbench = location.pathname.startsWith("/loop-mvp-lab")
		? "loop"
		: location.pathname.startsWith("/plan-lab") && planEnabled
			? "plan"
			: "workflow";
	const [workbench, setWorkbench] = useState<Workbench>(routeWorkbench);
	useEffect(() => {
		setWorkbench(routeWorkbench);
	}, [routeWorkbench]);

	return (
		<PasswordGate>
			<ToastProvider>
				<IntercomMessenger />
				<div className="min-h-screen bg-surface-background text-text-primary">
					<div className="flex min-h-screen flex-col lg:flex-row">
						<aside
							className={cn(
								"sticky top-0 z-20 flex max-h-[72vh] w-full flex-none flex-col border-b border-surface-border bg-surface-card transition-[width] duration-150 lg:h-screen lg:max-h-none lg:border-b-0 lg:border-r",
								sidebarCollapsed ? "lg:w-[88px]" : "lg:w-[272px]",
							)}
							data-testid="admin-sidebar-shell"
						>
							<div
								className={cn(
									"relative flex items-center border-b border-surface-border",
									sidebarCollapsed ? "justify-center px-3 py-5" : "justify-between gap-3 px-4 py-4",
								)}
							>
								<div
									className={cn(
										"flex min-w-0 items-center",
										sidebarCollapsed ? "justify-center" : "gap-3 pr-7",
									)}
									data-testid="admin-sidebar-brand"
									aria-label="wentao Admin Console"
								>
									<span
										className={cn(
											"flex flex-none items-center justify-center rounded-xl bg-brand-primary/10 font-semibold leading-none text-brand-primary shadow-sm",
											sidebarCollapsed ? "h-12 w-12 text-xl" : "h-10 w-10 text-lg",
										)}
										data-testid="admin-sidebar-logo"
									>
										韬
									</span>
									{sidebarCollapsed ? null : (
										<div className="min-w-0">
											<h1 className="truncate text-sm font-semibold text-text-primary">wentao</h1>
											<p className="truncate text-xs text-text-muted">Admin Console</p>
										</div>
									)}
								</div>
								<button
									type="button"
									className="z-10 inline-flex h-8 w-8 flex-none items-center justify-center rounded-full border border-surface-border bg-surface-background text-text-muted shadow-sm hover:bg-surface-subtle hover:text-text-primary lg:absolute lg:-right-4 lg:top-1/2 lg:-translate-y-1/2"
									onClick={() => setSidebarCollapsed((current) => !current)}
									aria-label={sidebarCollapsed ? "展开左侧菜单" : "收起左侧菜单"}
								>
									{sidebarCollapsed ? (
										<ChevronRight className="h-4 w-4" aria-hidden />
									) : (
										<ChevronLeft className="h-4 w-4" aria-hidden />
									)}
								</button>
							</div>

							<nav
								className={cn(
									"min-h-0 flex-1 overflow-y-auto",
									sidebarCollapsed ? "space-y-2 px-4 py-4" : "space-y-1 px-3 py-3",
								)}
								aria-label="Admin Console 左侧菜单"
							>
								{sidebarCollapsed ? null : (
									<div
										className="mb-2 flex gap-1 rounded-lg bg-surface-subtle p-1"
										data-testid="workbench-switch"
									>
										{(planEnabled ? WORKBENCHES : WORKBENCHES_NO_PLAN).map((wb) => (
											<button
												key={wb}
												type="button"
												onClick={() => setWorkbench(wb)}
												className={cn(
													// 三段后宽度紧:按文字分宽 + 不折行(D-30)
													"flex-auto whitespace-nowrap rounded-md px-1.5 py-1.5 text-xs font-medium transition-colors",
													workbench === wb
														? "bg-surface-card text-brand-primary shadow-sm"
														: "text-text-muted hover:text-text-primary",
												)}
												data-testid={`workbench-${wb}`}
											>
												{WORKBENCH_LABEL[wb]}
											</button>
										))}
									</div>
								)}
								{SIDEBAR_GROUPS.filter((group) => groupWorkbench(group.id) === workbench).map(
									(group) => {
										const Icon = group.icon;
										const directItem = getDirectSidebarItem(group);
										const groupActive = group.items.some((item) =>
											isRouteActive(location.pathname, item.to),
										);
										// Loop 多项组常展开;Workflow 多项组可折叠
										const collapsible = !directItem;
										const expanded =
											!collapsible || (expandedGroups[group.id] ?? false) || groupActive;

										// 收起态:图标轨
										if (sidebarCollapsed) {
											return (
												<section key={group.id} data-testid={group.testId}>
													{directItem ? (
														<Link
															to={directItem.to}
															hash={directItem.hash}
															// 带 hash 的单项组(Plan 链路总览)按 hash 判选中,不再整条路由都亮(D-30)
															activeOptions={{
																exact: directItem.to === "/",
																includeHash: Boolean(directItem.hash),
															}}
															className="mx-auto flex h-11 w-12 items-center justify-center rounded-xl text-text-muted hover:bg-surface-subtle hover:text-text-primary"
															activeProps={{ className: "bg-brand-accent/10 text-brand-accent" }}
															data-testid={directItem.testId}
															aria-label={group.label}
														>
															<Icon className="h-5 w-5" aria-hidden />
														</Link>
													) : (
														<div
															className="mx-auto flex h-11 w-12 items-center justify-center rounded-xl text-text-muted"
															title={group.label}
														>
															<Icon className="h-5 w-5" aria-hidden />
														</div>
													)}
												</section>
											);
										}

										// 展开态 · directItem 组 = 干净一行链接
										if (directItem) {
											return (
												<section key={group.id} data-testid={group.testId}>
													<Link
														to={directItem.to}
														hash={directItem.hash}
														// 带 hash 的单项组(Plan 链路总览)按 hash 判选中,不再整条路由都亮(D-30)
														activeOptions={{
															exact: directItem.to === "/",
															includeHash: Boolean(directItem.hash),
														}}
														className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-text-secondary transition-colors hover:bg-surface-subtle hover:text-text-primary"
														activeProps={{
															className: "bg-brand-accent/10 font-medium text-brand-accent",
														}}
														data-testid={directItem.testId}
														aria-label={group.label}
													>
														<Icon className="h-4 w-4 flex-none text-text-muted" aria-hidden />
														<span className="truncate">{group.label}</span>
													</Link>
												</section>
											);
										}

										// 展开态 · 多项组 = 轻量小标题 + 项
										return (
											<section key={group.id} className="pt-2" data-testid={group.testId}>
												{collapsible ? (
													<button
														type="button"
														onClick={() =>
															setExpandedGroups((current) => ({
																...current,
																[group.id]: !(current[group.id] ?? false),
															}))
														}
														className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm font-semibold text-text-primary transition-colors hover:bg-surface-subtle"
														aria-expanded={expanded}
													>
														<Icon className="h-4 w-4 flex-none text-text-muted" aria-hidden />
														<span className="flex-1 truncate text-left">{group.label}</span>
														<ChevronDown
															className={cn(
																"h-3.5 w-3.5 flex-none text-text-muted transition-transform",
																expanded ? "rotate-180" : "",
															)}
															aria-hidden
														/>
													</button>
												) : (
													<div className="flex items-center gap-2 px-2 py-1.5 text-sm font-semibold text-text-primary">
														<Icon className="h-4 w-4 flex-none text-text-muted" aria-hidden />
														<span className="truncate">{group.label}</span>
													</div>
												)}
												{expanded ? (
													<div className="mt-0.5 ml-3.5 space-y-0.5 border-l border-surface-border pl-2">
														{group.items.map((item) => (
															<SidebarNavItem
																key={`${group.id}-${item.label}`}
																item={item}
																isActive={isSidebarItemActive(
																	location.pathname,
																	location.hash,
																	item,
																)}
															/>
														))}
													</div>
												) : null}
											</section>
										);
									},
								)}
							</nav>
						</aside>

						<div className="min-w-0 flex-1">
							<main className="p-4 lg:p-6">
								<Outlet />
							</main>
						</div>
					</div>
				</div>
			</ToastProvider>
		</PasswordGate>
	);
}

function SidebarNavItem({ item, isActive }: { item: SidebarItem; isActive: boolean }) {
	if (!item.to) {
		return (
			<div className="flex min-h-7 items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-[13px] leading-5 text-text-muted">
				<span className="truncate">{item.label}</span>
				<span className="flex-none text-[11px] text-text-muted">待开发</span>
			</div>
		);
	}

	return (
		<Link
			to={item.to}
			hash={item.hash}
			activeOptions={{ exact: item.to === "/" }}
			className={cn(
				"relative flex min-h-7 items-center rounded-md px-2.5 py-1.5 text-[13px] leading-5 text-text-secondary transition-colors hover:bg-surface-subtle/70 hover:text-text-primary",
				isActive
					? "bg-surface-card font-medium text-text-primary shadow-sm ring-1 ring-surface-border before:absolute before:inset-y-1.5 before:left-0 before:w-[3px] before:rounded-full before:bg-brand-accent"
					: "",
			)}
			data-active={isActive ? "true" : "false"}
			data-testid={item.testId}
		>
			<span className="truncate">{item.label}</span>
		</Link>
	);
}
